import { clearStored, readStored, writeStored } from '@/shared/lib/storage'
/**
 * White-label mode.
 *
 * Deliberately per-browser, not per-account and not global. The point is to let
 * one admin present the CRM under Solvigo's brand — a demo, a screenshot, a
 * pitch — without any of the BDRs' screens changing. Storing it server-side
 * would need a per-user column and would still follow the account onto a shared
 * machine; storing it globally would re-skin the product for the whole team.
 * localStorage is the only option that cannot leak to another viewer, which is
 * the actual requirement.
 *
 * The cost is that it does not follow you to another browser or an incognito
 * window. That is the right trade here: turning it back on is one click, and
 * the failure mode is "my demo laptop is not branded" rather than "I have
 * re-skinned the product for the sales team".
 */
export type BrandKey = 'yllw' | 'solvigo'

export interface Brand {
  key: BrandKey
  /** Shown wherever the product names its owner. */
  name: string
  /** The wordmark beside the logo in the top bar. */
  product: string
  /** The lead-source product, e.g. "YLLW Radar" in the handover card. */
  radar: string
  /** Browser tab. */
  documentTitle: string
}

export const BRANDS: Record<BrandKey, Brand> = {
  yllw: {
    key: 'yllw',
    name: 'YLLW',
    product: 'BDR',
    radar: 'YLLW Radar',
    documentTitle: 'YLLW BDR',
  },
  solvigo: {
    key: 'solvigo',
    name: 'Solvigo',
    product: 'CRM',
    radar: 'Solvigo Radar',
    documentTitle: 'Solvigo CRM',
  },
}

const KEY = 'brand'

function isBrandKey(v: unknown): v is BrandKey {
  return v === 'yllw' || v === 'solvigo'
}

export function loadBrand(): BrandKey {
  const v = readStored(KEY, { legacyKey: 'crm.brand' })
  return isBrandKey(v) ? v : 'yllw'
}

export function persistBrand(key: BrandKey): void {
  if (key === 'yllw') clearStored(KEY)
  else writeStored(KEY, key)
}
